import React from "react";
import { Image, Path, Svg, View, StyleSheet } from "@react-pdf/renderer";
import type { TopContentBucket } from "../topContent/types";
import type { PeriodSummaryPlatformKey } from "../metricsReportWeb/buildPeriodSummaryData";
import { getPdfPodscopeSquareSrc } from "./pdfAssets";
import { usePdfTheme } from "./PdfThemeContext";
import { PDF_CARD_BORDER_WIDTH } from "./pdfTheme";

/** Same path as `@mui/icons-material/Instagram`. */
const INSTAGRAM_MUI_PATH =
  "M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z";

/** YouTube play-button mark (Simple Icons). */
const YOUTUBE_PATH =
  "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z";

/** YouTube Shorts arms + play triangle (matches web YouTubeShortsIcon). */
const SHORTS_ARMS_PATH =
  "m18.931 9.99-1.441-.601 1.717-.913a4.48 4.48 0 0 0 1.874-6.078 4.506 4.506 0 0 0-6.09-1.874L4.792 5.929a4.504 4.504 0 0 0-2.402 4.193 4.521 4.521 0 0 0 2.666 3.904c.036.012 1.442.6 1.442.6l-1.706.901a4.51 4.51 0 0 0-2.369 3.967A4.528 4.528 0 0 0 6.93 24c.725 0 1.437-.174 2.08-.508l10.21-5.406a4.494 4.494 0 0 0 2.39-4.192 4.525 4.525 0 0 0-2.678-3.904Z";
const SHORTS_PLAY_PATH = "M9.597 15.19V8.824l6.007 3.184z";

/** TikTok glyph (Simple Icons). */
const TIKTOK_PATH =
  "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z";

/** Facebook f-mark (Material Icons). */
const FACEBOOK_PATH =
  "M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z";

const ICON_SIZE = 18;
const PODSCOPE_RADIUS = 4;

const styles = StyleSheet.create({
  podscopeImage: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    objectFit: "cover",
  },
  iconSlot: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    flexShrink: 0,
  },
});

export function MetricsPdfInstagramIcon({
  size = ICON_SIZE,
  color = "#E1306C",
}: {
  size?: number;
  color?: string;
}): React.ReactElement {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path d={INSTAGRAM_MUI_PATH} fill={color} />
    </Svg>
  );
}

export function MetricsPdfYoutubeIcon({
  size = ICON_SIZE,
  color = "#FF0000",
}: {
  size?: number;
  color?: string;
}): React.ReactElement {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path d={YOUTUBE_PATH} fill={color} />
    </Svg>
  );
}

export function MetricsPdfYoutubeShortsIcon({
  size = ICON_SIZE,
}: {
  size?: number;
}): React.ReactElement {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path d={SHORTS_ARMS_PATH} fill="#FF0000" />
      <Path d={SHORTS_PLAY_PATH} fill="#FFFFFF" />
    </Svg>
  );
}

export function MetricsPdfTiktokIcon({
  size = ICON_SIZE,
  color = "#000000",
}: {
  size?: number;
  color?: string;
}): React.ReactElement {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path d={TIKTOK_PATH} fill={color} />
    </Svg>
  );
}

export function MetricsPdfFacebookIcon({
  size = ICON_SIZE,
  color = "#1877F2",
}: {
  size?: number;
  color?: string;
}): React.ReactElement {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path d={FACEBOOK_PATH} fill={color} />
    </Svg>
  );
}

/** Platform logos for Desglose por plataforma — SVG so react-pdf does not need remote fetches. */
export function MetricsPdfPlatformBreakdownLogo({
  platformKey,
  marginRight = 6,
}: {
  platformKey: PeriodSummaryPlatformKey;
  marginRight?: number;
}): React.ReactElement {
  const slotStyle = [styles.iconSlot, ...(marginRight ? [{ marginRight }] : [])];

  if (platformKey === "youtube_shorts") {
    return (
      <View style={slotStyle}>
        <MetricsPdfYoutubeShortsIcon />
      </View>
    );
  }
  if (platformKey === "instagram") {
    return (
      <View style={slotStyle}>
        <MetricsPdfInstagramIcon />
      </View>
    );
  }
  if (platformKey === "tiktok") {
    return (
      <View style={slotStyle}>
        <MetricsPdfTiktokIcon />
      </View>
    );
  }
  if (platformKey === "facebook") {
    return (
      <View style={slotStyle}>
        <MetricsPdfFacebookIcon />
      </View>
    );
  }
  return (
    <View style={slotStyle}>
      <MetricsPdfYoutubeIcon />
    </View>
  );
}

export function MetricsPdfPodscopeSquareLogo({
  marginRight = 0,
}: {
  marginRight?: number;
}): React.ReactElement {
  const theme = usePdfTheme();
  const podscopeWrap = React.useMemo(
    () =>
      StyleSheet.create({
        wrap: {
          width: ICON_SIZE,
          height: ICON_SIZE,
          borderRadius: PODSCOPE_RADIUS,
          borderWidth: PDF_CARD_BORDER_WIDTH,
          borderColor: theme.cardBorder,
          overflow: "hidden",
          flexShrink: 0,
        },
      }),
    [theme],
  );

  return (
    <View style={[podscopeWrap.wrap, ...(marginRight ? [{ marginRight }] : [])]}>
      <Image src={getPdfPodscopeSquareSrc()} style={styles.podscopeImage} />
    </View>
  );
}

export function TopContentPdfPlatformLogo({
  bucket,
}: {
  bucket: TopContentBucket;
}): React.ReactElement {
  const slotStyle = { marginRight: 8 };

  if (bucket === "youtube_livestream") {
    return <MetricsPdfPodscopeSquareLogo marginRight={8} />;
  }

  if (bucket === "instagram") {
    return (
      <View style={[styles.iconSlot, slotStyle]}>
        <MetricsPdfInstagramIcon />
      </View>
    );
  }

  if (bucket === "tiktok") {
    return (
      <View style={[styles.iconSlot, slotStyle]}>
        <MetricsPdfTiktokIcon />
      </View>
    );
  }

  if (bucket === "facebook") {
    return (
      <View style={[styles.iconSlot, slotStyle]}>
        <MetricsPdfFacebookIcon />
      </View>
    );
  }

  return (
    <View style={[styles.iconSlot, slotStyle]}>
      <MetricsPdfYoutubeIcon />
    </View>
  );
}
